import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import { motion } from "framer-motion";
import inariLogo from "../../static/media/StrategicAllies/inari.webp";
import sushiBanner from "../../static/media/StrategicAllies/sushi_banner.mp4";

export default function InariPartner() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    if (!videoRef.current) return;
    const nextMuted = !videoRef.current.muted;
    videoRef.current.muted = nextMuted;
    videoRef.current.volume = 1;
    setMuted(nextMuted);
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        py: { xs: 5, md: 8 },
        bgcolor: "#000000",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: { xs: "92%", md: "86%" },
          maxWidth: "1240px",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            position: "relative",
            borderRadius: { xs: "24px", md: "34px" },
            overflow: "hidden",
            background:
              "linear-gradient(135deg, #040404 0%, #090909 34%, #170003 64%, #000000 100%)",
            boxShadow:
              "0 28px 90px rgba(0,0,0,0.48), 0 0 0 1px rgba(255,255,255,0.05) inset",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 16% 16%, rgba(255,255,255,0.08) 0%, transparent 24%), radial-gradient(circle at 90% 24%, rgba(229,9,20,0.22) 0%, transparent 30%), radial-gradient(circle at 82% 78%, rgba(229,9,20,0.16) 0%, transparent 26%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1.05fr) minmax(320px, 420px)",
              },
              alignItems: "center",
              gap: { xs: 3, md: 2.5, lg: 4 },
              px: { xs: 2.2, sm: 3, md: 3.6, lg: 4.2 },
              py: { xs: 3, md: 3.5, lg: 4 },
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                pr: { xs: 0, md: 0.5, lg: 1.5 },
              }}
            >
              <Stack
                spacing={{ xs: 2.1, md: 2.3 }}
                sx={{
                  alignItems: { xs: "center", md: "flex-start" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <motion.img
                  src={inariLogo}
                  alt="Inari Sushi"
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.6 }}
                  style={{
                    width: "100%",
                    maxWidth: "170px",
                    display: "block",
                    filter:
                      "drop-shadow(0 0 20px rgba(255,255,255,0.22)) drop-shadow(0 12px 34px rgba(255,255,255,0.1))",
                  }}
                />

                <Typography
                  sx={{
                    color: "#ffffff",
                    fontWeight: 800,
                    letterSpacing: -0.7,
                    fontSize: { xs: 28, md: 34, lg: 38 },
                    lineHeight: 1.04,
                    maxWidth: { xs: "100%", md: 620 },
                  }}
                >
                  Beneficios exclusivos para miembros activos NovaXFit
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.86)",
                    fontSize: { xs: 15.5, md: 16.2, lg: 16.8 },
                    fontWeight: 600,
                    lineHeight: 1.76,
                    maxWidth: { xs: "100%", md: 620 },
                  }}
                >
                  Inari Sushi ofrece descuentos especiales para los miembros de
                  NovaXFit en su carta de comida. Incluye un 15% de descuento en
                  todos los platos de la carta.
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.68)",
                    fontSize: { xs: 14.6, md: 15.1, lg: 15.4 },
                    fontWeight: 600,
                    lineHeight: 1.76,
                    maxWidth: { xs: "100%", md: 620 },
                  }}
                >
                  Aplica únicamente para miembros activos de NovaXFit. Válido
                  solo en platos de comida. No aplica en promociones, bebidas ni
                  productos promocionales.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.4}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    pt: 0.4,
                    justifyContent: { xs: "center", md: "flex-start" },
                    alignItems: "center",
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    style={{ width: "100%" }}
                  >
                    <Button
                      component="a"
                      href="https://www.instagram.com/inarisushiec?igsh=MWN0eHJvemd6anBqMg=="
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<InstagramIcon />}
                      sx={{
                        minHeight: 52,
                        width: { xs: "100%", sm: "auto" },
                        textTransform: "none",
                        fontWeight: 900,
                        borderRadius: "16px",
                        px: 2.8,
                        fontSize: 14.8,
                        color: "#ffffff",
                        background:
                          "linear-gradient(135deg, #7f0008 0%, #e50914 48%, #330005 100%)",
                        boxShadow: "0 16px 40px rgba(229,9,20,0.32)",
                        "&:hover": {
                          filter: "brightness(1.06)",
                        },
                      }}
                    >
                      Instagram
                    </Button>
                  </motion.div>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.56)",
                      fontSize: { xs: 13.3, md: 13.6 },
                      fontWeight: 700,
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    Presenta tu membresía activa para acceder al beneficio
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: { xs: 300, sm: 330, md: 360, lg: 390 },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7 }}
                  style={{ width: "100%" }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      borderRadius: { xs: "22px", md: "26px" },
                      overflow: "hidden",
                      background: "#050505",
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow:
                        "0 24px 60px rgba(0,0,0,0.34), 0 8px 30px rgba(229,9,20,0.18)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "9 / 16",
                        maxHeight: { xs: "62vh", md: 560, lg: 600 },
                        bgcolor: "#000",
                      }}
                    >
                      <Box
                        component="video"
                        ref={videoRef}
                        autoPlay
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                        controlsList="nofullscreen noremoteplayback nodownload"
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "cover",
                          backgroundColor: "#000",
                          pointerEvents: "none",
                        }}
                      >
                        <source src={sushiBanner} type="video/mp4" />
                      </Box>
                    </Box>

                    <IconButton
                      onClick={toggleSound}
                      sx={{
                        position: "absolute",
                        right: 14,
                        bottom: 14,
                        width: 44,
                        height: 44,
                        bgcolor: "rgba(0,0,0,0.58)",
                        color: "#fff",
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        zIndex: 2,
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.72)",
                        },
                      }}
                    >
                      {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
                    </IconButton>
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 2.8, md: 3.4 },
            borderRadius: { xs: "22px", md: "28px" },
            background: "#0b0b0b",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 16px 42px rgba(0,0,0,0.24)",
            px: { xs: 2.2, md: 3.2 },
            py: { xs: 2.2, md: 2.7 },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2, md: 3 }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  borderColor: "rgba(255,255,255,0.08)",
                }}
              />
            }
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  fontSize: { xs: 18, md: 19 },
                  mb: 1,
                }}
              >
                Términos y condiciones
              </Typography>

              <Stack spacing={0.85}>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: { xs: 14.1, md: 14.7 },
                    fontWeight: 700,
                    lineHeight: 1.55,
                  }}
                >
                  Aplica únicamente para miembros activos de NovaXFit.
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: { xs: 14.1, md: 14.7 },
                    fontWeight: 700,
                    lineHeight: 1.55,
                  }}
                >
                  Válido solo en platos de comida. No aplica en promociones,
                  bebidas ni productos promocionales.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                minWidth: { xs: "auto", md: 250 },
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
              }}
            >
              <motion.div
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.985 }}
                style={{ width: "100%" }}
              >
                <Button
                  component="a"
                  href="https://www.instagram.com/inarisushiec?igsh=MWN0eHJvemd6anBqMg=="
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  endIcon={<ArrowOutwardRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: "14px",
                    px: 2.4,
                    py: 1.15,
                    fontSize: 14.5,
                    color: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Ver contacto
                </Button>
              </motion.div>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}